import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { AlertTriangle, LogOut, UserCircleIcon } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import Avatar from '@/component-library/primitives/Avatar/Avatar';
import DropDown from '@/component-library/primitives/DropDown/DropDown';
import CheckListIcon from '@/component-library/primitives/Icons/CheckListIcon/CheckListIcon';
import ChevronNewIcon from '@/component-library/primitives/Icons/ChevronNewIcon/ChevronNewIcon';
import FilesIcon from '@/component-library/primitives/Icons/FilesIcon/FilesIcon';
import FolderIcon from '@/component-library/primitives/Icons/FolderIcon/FolderIcon';
import GearIcon from '@/component-library/primitives/Icons/GearIcon/GearIcon';
import HelpIcon from '@/component-library/primitives/Icons/HelpIcon/HelpIcon';
import HouseIcon from '@/component-library/primitives/Icons/HouseIcon/HouseIcon';
import LogoIcon from '@/component-library/primitives/Icons/LogoIcon/LogoIcon';
import NPSIcon from '@/component-library/primitives/Icons/NPSIcon/NPSIcon';
import PaperPlaneIcon from '@/component-library/primitives/Icons/PaperPlaneIcon/PaperPlaneIcon';
import SignOutIcon from '@/component-library/primitives/Icons/SignOut/SignOut';
import UsersIcon from '@/component-library/primitives/Icons/UsersIcon/UsersIcon';
import VolumeIcon from '@/component-library/primitives/Icons/VolumeIcon/VolumeIcon';
import WindowSidebarIcon from '@/component-library/primitives/Icons/WindowSidebarIcon/WindowSidebarIcon';
import { useSingleSendsEnabled } from '@/hooks/useSingleSendsEnabled';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { AppRoutes } from '@/utils/routes/router';

import { selectIsCollapsableIconHidden, selectIsCollapsed, setCollapsedSidebar } from './sideMenuSlice';

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
}

const NavLink = ({ to, children, collapsedSidebar }: NavLinkProps & { collapsedSidebar: boolean }) => {
  const isActive = useMatch(`${to}/*`);

  return (
    <Link
      to={to}
      className={`group [&>*_path]:stroke-custom-black flex py-2 px-[0.375rem] items-center text-custom-black rounded-lg ${
        isActive ? 'bg-custom-mediumSilver' : 'hover:bg-custom-lightSilver'
      } ${collapsedSidebar && 'justify-center'}`}
    >
      {children}
    </Link>
  );
};

const Divider = () => <hr className="border-custom-mediumSilver m-1" />;

const SideMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSingleSendsOpen, setIsSingleSendsOpen] = useState(false);
  const [showCollapsableIcon, setShowCollapsableIcon] = useState(true);
  const collapsedSidebar = useAppSelector(selectIsCollapsed);
  const isCollapsableIconHidden = useAppSelector(selectIsCollapsableIconHidden);
  const dispatch = useAppDispatch();
  const { user, signOut } = useAuth();
  const singleSendsEnabled = useSingleSendsEnabled();
  const wasCollapsedRef = useRef(collapsedSidebar);

  const currentLocation = useLocation();
  const { pathname } = currentLocation;

  const isInSingleSendsSection =
    singleSendsEnabled &&
    (pathname === AppRoutes.singleSendsRoot || pathname.startsWith(`${AppRoutes.singleSendsRoot}/`));

  useEffect(() => {
    if (collapsedSidebar) {
      setIsSingleSendsOpen(false);
    } else if (wasCollapsedRef.current && isInSingleSendsSection) {
      setIsSingleSendsOpen(true);
    }
    wasCollapsedRef.current = collapsedSidebar;
  }, [collapsedSidebar, isInSingleSendsSection]);

  // Only close the Single Sends submenu when leaving the whole section (not when clicking items inside it).
  useEffect(() => {
    if (!isInSingleSendsSection) {
      setIsSingleSendsOpen(false);
    }
  }, [isInSingleSendsSection]);

  const collapseSidebar = () => {
    dispatch(setCollapsedSidebar(!collapsedSidebar));
    setIsSingleSendsOpen(false);
  };

  const options = [
    {
      icon: <UserCircleIcon width="20" height="20" />,
      text: t('HeaderLoggedIn.personalAccount'),
      key: 'personalAccount',
    },
    {
      icon: <LogOut width="20" height="20" />,
      text: t('HeaderLoggedIn.optionSignOut'),
      key: 'signOut',
    },
  ];

  const renderNavLink = (
    collapsedSidebar: boolean,
    to: string,
    icon: JSX.Element,
    text: string,
    classNames = 'text-sm mb-[2px]',
  ) => (
    <li className={classNames}>
      <NavLink to={to} collapsedSidebar={collapsedSidebar}>
        <div className="relative flex flex-row items-center">
          <span className="group-hover:[&>*_path]:stroke-custom-black">{icon}</span>
          <span
            className={`pl-2 ${
              collapsedSidebar &&
              `absolute flex items-center pr-3 px-[0.25rem] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[0.1s] linear overflow-hidden ${
                pathname === to
                  ? 'bg-custom-mediumSilver after:border-r-custom-mediumSilver'
                  : 'bg-custom-lightSilver after:border-r-custom-lightSilver'
              } left-[1.5rem] rounded-r w-max z-[51] h-[calc(100%+1rem)]`
            }`}
          >
            {text}
          </span>
        </div>
      </NavLink>
    </li>
  );

  return (
    <div
      className={`sidebar flex flex-col relative shrink-0 ${
        collapsedSidebar ? 'w-[4rem] transition-[width] duration-300' : 'w-[16rem]'
      } text-gray-500`}
      // onMouseEnter={() => !isCollapsableIconHidden && setShowCollapsableIcon(true)}
      // onMouseLeave={() => !isCollapsableIconHidden && setShowCollapsableIcon(false)}
    >
      {/* Top section */}
      <div
        className={`${collapsedSidebar && '[&_svg]:w-[2.625rem]'} flex items-center ${
          collapsedSidebar ? 'ml-3 mr-3' : 'ml-6 mr-6'
        } cursor-pointer pt-6 pb-2`}
        onClick={() => navigate(AppRoutes.dashboard)}
      >
        {collapsedSidebar ? <LogoIcon width="40" height="40" /> : <LogoIcon withText height="40" />}
      </div>
      {showCollapsableIcon && (
        <div className={`absolute top-[3.5rem] right-0 translate-x-[50%]`}>
          <div
            onClick={collapseSidebar}
            className="group w-8 h-8 rounded-full bg-custom-silver flex items-center justify-center cursor-pointer transition"
          >
            <span
              className={`group-hover:[&>*_path]:stroke-custom-black [&>*_path]:stroke-custom-black transform transition-transform duration-200 ${
                collapsedSidebar ? 'group-hover:translate-x-1' : 'group-hover:-translate-x-1'
              }`}
            >
              <ChevronNewIcon type={collapsedSidebar ? 'right' : 'left'} />
            </span>
          </div>
        </div>
      )}

      {/* Links */}
      <nav
        className={` ${
          collapsedSidebar
            ? 'mx-3 my-2'
            : 'mx-4 my-3 overflow-y-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar-thumb]:bg-gray-600 ml-4'
        }`}
      >
        {!collapsedSidebar && (
          <p className="pl-2 text-[11px] font-bold mb-2 tracking-[0.025rem] text-cs-darkGray">
            {t('SideMenu.mainSection')}
          </p>
        )}
        <ul className="lg:relative 3xl:static">
          {/* First portion of links */}
          {renderNavLink(collapsedSidebar, AppRoutes.dashboard, <HouseIcon />, `${t('SideMenu.linkDashboard')}`)}
          {renderNavLink(collapsedSidebar, AppRoutes.members, <UsersIcon />, 'Users')}
          {renderNavLink(collapsedSidebar, AppRoutes.signals, <AlertTriangle width="20" height="20" />, 'Signals')}
          {renderNavLink(collapsedSidebar, AppRoutes.tickets, <FilesIcon />, 'Tickets')}
          {renderNavLink(collapsedSidebar, AppRoutes.segments, <FolderIcon />, 'User Groups')}
          {renderNavLink(
            collapsedSidebar,
            AppRoutes.activityLogs,
            <CheckListIcon />,
            `${t('SideMenu.linkActivityLogs')}`,
          )}

          {singleSendsEnabled && (
            <>
              {collapsedSidebar ? (
                <Divider />
              ) : (
                <p className="mt-5 pl-2 text-[11px] font-bold mb-2 tracking-[0.025rem] text-cs-darkGray">
                  {t('SideMenu.messagesSection')}
                </p>
              )}
              {renderNavLink(collapsedSidebar, AppRoutes.delivery, <VolumeIcon />, `${t('SideMenu.linkDelivery')}`)}
              <li className="text-sm mb-[2px]">
                {collapsedSidebar ? (
                  <Link
                    to={AppRoutes.singleSendsRoot}
                    title={t('SideMenu.linkBuildMessage')}
                    className="group [&>*_path]:stroke-custom-black flex py-2 px-[0.375rem] items-center text-custom-black rounded-lg hover:bg-custom-lightSilver justify-center"
                  >
                    <PaperPlaneIcon color="#9bafd0" />
                  </Link>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsSingleSendsOpen((o) => !o)}
                      className={`group flex w-full py-2 px-[0.375rem] items-center text-custom-black rounded-lg text-left ${
                        isInSingleSendsSection ? 'bg-custom-mediumSilver' : 'hover:bg-custom-lightSilver'
                      }`}
                    >
                      <span className="[&>*_path]:stroke-custom-black shrink-0">
                        <PaperPlaneIcon color="#9bafd0" />
                      </span>
                      <span className="pl-2 flex-1 text-sm">{t('SideMenu.linkBuildMessage')}</span>
                      <ChevronNewIcon
                        color="#9bafd0"
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        type={isSingleSendsOpen ? 'up' : 'down'}
                      />
                    </button>
                    {isSingleSendsOpen && (
                      <ul className="mt-1 ml-2 pl-3 border-l border-custom-mediumSilver space-y-0.5">
                        {[
                          { to: AppRoutes.newEmail, label: t('SideMenu.linkEmail') },
                          { to: AppRoutes.newSMS, label: t('SideMenu.linkSMS') },
                          { to: AppRoutes.newPush, label: t('SideMenu.linkPushNotification') },
                          { to: AppRoutes.singleSendsMetrics, label: t('SideMenu.linkSingleSendsMetrics') },
                        ].map(({ to, label }) => (
                          <li key={to}>
                            <Link
                              to={to}
                              className={`block py-1.5 px-2 text-sm rounded-md ${
                                pathname === to || pathname.startsWith(`${to}/`)
                                  ? 'bg-custom-mediumSilver text-custom-black font-medium'
                                  : 'text-custom-black hover:bg-custom-lightSilver'
                              }`}
                            >
                              {label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            </>
          )}

          {/* Second portion of links */}
          {collapsedSidebar ? (
            <Divider />
          ) : (
            <p className="mt-5 pl-2 text-[11px] font-bold mb-2 tracking-[0.025rem] text-cs-darkGray">
              {t('SideMenu.settingsSection')}
            </p>
          )}
          {renderNavLink(collapsedSidebar, AppRoutes.settings, <GearIcon />, `${t('SideMenu.linkSettings')}`)}
          {renderNavLink(collapsedSidebar, AppRoutes.help, <HelpIcon />, `${t('SideMenu.linkHelp')}`)}
        </ul>
      </nav>

      {/* Sidemenu Footer */}
      <div className={`${collapsedSidebar ? 'mx-3' : 'mx-6'} mt-auto mb-6 flex`}>
        <DropDown
          options={options}
          trigger={<Avatar initials={user?.attributes.given_name} size="small" />}
          onSelect={(option: any) => {
            switch (option.key) {
              case 'personalAccount':
                navigate(AppRoutes.personalAccount);
                break;
              case 'signOut':
                void signOut().then(() => {
                  if (!isClerkAuthEnabled()) {
                    navigate(AppRoutes.home, { replace: true });
                  }
                });
                break;

              default:
                break;
            }
          }}
        />

        <div className="w-[calc(100%-2rem)] pl-2">
          <h4 className="text-ellipsis whitespace-nowrap overflow-hidden text-sm text-custom-black">
            {user?.attributes?.given_name} {user?.attributes?.family_name}
          </h4>
          <div className="text-ellipsis whitespace-nowrap overflow-hidden text-xs text-custom-darkSilver font-semibold">
            {user?.attributes?.email}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
